<template>
  <div
    ref="wrapRef"
    class="widget-dropdown"
    :aria-expanded="internalValue"
  >
    <slot />

    <q-menu
      ref="qMenu"
      :model-value="internalValue"
      :class="dropdownClassName"
      :target="target"
      :max-height="maxHeight"
      :style="qMenuStyle"
      :auto-close="closeOnContentClick"
      :no-parent-event="disabled"
      :persistent="!closeOnOutsideClick"
      @update:model-value="toggle"
      @before-show="beforeShow"
      @hide="onHide"
    >
      <div
        ref="innerMenuRef"
        class="widget-dropdown__main"
      >
        <widget-button
          v-if="showClose"
          class="widget-dropdown__close"
          size="20"
          is-icon-only
          view="transparent"
          color="secondary"
          @click="close"
        >
          <widget-icon
            name="close"
            size="14px"
          />
        </widget-button>

        <slot
          name="menu"
          v-bind="{ close, qMenu, updatePosition }"
        />
      </div>
    </q-menu>
  </div>
</template>

<script lang="ts">
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import type { PropType } from '@/composition/vue/compositionApi'
import { computed, defineComponent, nextTick, ref, watch } from '@/composition/vue/compositionApi'
import type { FakeQMenu, ControlPosition } from '@/composition/tooltip/renderTooltip'
import { getTooltipAngleAttrs, renderTooltip } from '@/composition/tooltip/renderTooltip'
import { dropdownClassName, dropdownSizes } from '@/components/shared/types'
import { useResizeObserver } from '@/composition/resizeObserver'

export default defineComponent({
  components: {
    WidgetIcon,
    WidgetButton,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    closeOnContentClick: {
      type: Boolean,
      default: true,
    },
    closeOnOutsideClick: {
      type: Boolean,
      default: true,
    },
    maxHeight: {
      type: String,
      default: dropdownSizes.maxHeight.md,
    },
    minWidth: {
      type: String,
    },
    showClose: {
      type: Boolean,
    },
    target: {
      type: HTMLElement,
    },
    offset: {
      type: Array as PropType<number[]>,
    },
    position: {
      type: String as PropType<ControlPosition>,
      default: 'bottom-right',
    },
    disabled: {
      type: Boolean,
    },
    readonly: {
      type: Boolean,
    },
  },
  emits: {
    'update:model-value': (_val: boolean) => true,
    show: () => true,
    hide: (_val: MouseEvent) => true,
  },
  setup(props, context: any) {
    const qMenu = ref<FakeQMenu>()
    const wrapRef = ref()
    const innerMenuRef = ref()
    const internalValue = ref(props.modelValue)
    const { show, hide } = renderTooltip('tm-dropdown')

    const qMenuStyle = computed(() => {
      if (!props.minWidth) {
        return {}
      }
      // don't set the minWidth value == null/undefined https://textmagic.atlassian.net/browse/CAR-3192
      return {
        minWidth: props.minWidth,
      }
    })

    const toggle = (shown: boolean) => {
      internalValue.value = props.readonly ? false : shown
    }

    watch(
      () => props.modelValue,
      (newValue) => {
        internalValue.value = newValue
      },
    )
    watch(
      () => internalValue.value,
      (newValue) => {
        context.emit('update:model-value', newValue)
      },
    )

    const beforeShow = async () => {
      const anchorEl = props.target || wrapRef.value
      // for correct angle display need event between before-show and show, one nextTick not enough
      await nextTick()
      const currentMaxHeight = innerMenuRef.value?.parentElement.offsetHeight
      await nextTick()
      const menuEl = innerMenuRef.value.parentElement

      context.emit('show')

      const angleAttrs = getTooltipAngleAttrs('tm-dropdown', props.position, false)

      show({
        anchorEl,
        menuEl,
        attrs: angleAttrs,
        qMenu,
        maxHeight: Math.min(currentMaxHeight, parseInt(props.maxHeight!, 10)),
      })
    }
    const onHide = (e: MouseEvent) => {
      context.emit('hide', e)
      hide()
    }

    const close = () => {
      internalValue.value = false
    }

    const updatePosition = () => qMenu.value?.updatePosition()

    useResizeObserver(innerMenuRef, updatePosition)

    const getMenuWrapperElement = () => {
      return innerMenuRef.value
    }

    return {
      dropdownClassName,
      qMenuStyle,
      qMenu,
      wrapRef,
      innerMenuRef,
      internalValue,
      beforeShow,
      toggle,
      onHide,
      close,
      // public
      updatePosition,
      getMenuWrapperElement,
    }
  },
})
</script>

<!-- eslint-disable-next-line vue/enforce-style-attribute -->
<style lang="scss">
// Here we have non-scoped style because dropdown appears at the end on body without special uniq attr
// so scoped styles doesn't work
@import '@/styles/mixins.scss';

.widget-dropdown {
  display: inline-block;

  &__main {
    @include thumb-without-track;
    position: relative;
    overflow: auto;
    border-radius: $border-radius;
    box-shadow: $box-shadow;
    background: $white;
    line-height: 1.5;
  }

  &__close {
    position: absolute !important;
    top: 5px !important;
    right: 5px !important;
  }
}
</style>
